import { useState, useMemo, useEffect, useCallback, useRef, useLayoutEffect, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  MdSearch,
  MdDelete,
  MdEdit,
  MdClose,
  MdPersonAdd,
  MdFilterList,
  MdPeople,
  MdCheckCircle,
  MdOutlinePending,
  MdShield,
  MdExpandMore,
  MdCheck,
} from "react-icons/md";
import { ClearFiltersButton, TableBodySkeleton } from "@euroasiann/shared/ui";
import { useDebouncedSearch } from "@euroasiann/shared";
import { Pagination } from "../../components/shared/Pagination";

const API_URL = "/api/v1"; // Relative path is safer as per standard portal patterns
const FIXED_PORTAL = "admin";

type AssignOption = {
  value: string;
  label: string;
  sublabel?: string;
};

/* 🔐 AUTH FETCH */
const authFetch = (url: string, options: RequestInit = {}) => {
  const token = localStorage.getItem("accessToken");

  return fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
};

function AssignDropdown({
  value,
  onChange,
  placeholder,
  options = [],
  loadOptions,
  searchable = false,
}: {
  value: AssignOption | null;
  onChange: (option: AssignOption | null) => void;
  placeholder: string;
  options?: AssignOption[];
  loadOptions?: (query: string) => Promise<AssignOption[]>;
  searchable?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [asyncOptions, setAsyncOptions] = useState<AssignOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const items = loadOptions ? asyncOptions : options.filter((option) => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return (
      option.label.toLowerCase().includes(q) ||
      (option.sublabel || "").toLowerCase().includes(q)
    );
  });

  const updateMenuPosition = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const spaceBelow = Math.max(140, viewportH - rect.bottom - 12);
    setMenuStyle({
      position: "fixed",
      zIndex: 2300,
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      maxHeight: Math.min(280, spaceBelow),
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const onMove = () => updateMenuPosition();
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [open, items.length, loading]);

  useEffect(() => {
    if (!open || !loadOptions) return;
    let cancelled = false;
    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const next = await loadOptions(query);
        if (!cancelled) setAsyncOptions(next);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, query ? 200 : 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, query, loadOptions]);

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (buttonRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
      setQuery("");
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative w-full">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex h-10 w-full items-center justify-between gap-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-left text-sm transition-colors hover:border-[#0088cc] focus:border-[#0088cc] focus:outline-none"
      >
        <span className={`min-w-0 truncate ${value ? "text-[hsl(var(--foreground))]" : "text-[hsl(var(--muted-foreground))]"}`}>
          {value?.label || placeholder}
        </span>
        <MdExpandMore className="h-4 w-4 shrink-0 text-[hsl(var(--muted-foreground))]" />
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              role="listbox"
              style={menuStyle}
              className="flex flex-col overflow-hidden rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-xl"
            >
              {searchable ? (
                <div className="flex shrink-0 items-center gap-2 border-b border-[hsl(var(--border))] px-2.5 py-2">
                  <MdSearch className="h-4 w-4 shrink-0 text-[hsl(var(--muted-foreground))]" />
                  <input
                    ref={searchRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search..."
                    className="w-full bg-transparent text-sm text-[hsl(var(--foreground))] outline-none placeholder:text-[hsl(var(--muted-foreground))]"
                  />
                </div>
              ) : null}
              <div className="min-h-0 flex-1 overflow-y-auto py-1">
                {loading ? (
                  <div className="px-3 py-3 text-xs text-[hsl(var(--muted-foreground))]">Loading…</div>
                ) : items.length === 0 ? (
                  <div className="px-3 py-3 text-xs text-[hsl(var(--muted-foreground))]">No matches</div>
                ) : (
                  items.map((option) => {
                    const selected = value?.value === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        onClick={() => {
                          onChange(option);
                          setOpen(false);
                          setQuery("");
                        }}
                        className={`flex w-full items-start gap-2 px-3 py-2 text-left ${
                          selected ? "bg-[#0088cc]/10" : "hover:bg-[hsl(var(--muted))]"
                        }`}
                      >
                        <MdCheck className={`mt-0.5 h-4 w-4 shrink-0 ${selected ? "text-[#0088cc]" : "text-transparent"}`} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-[hsl(var(--foreground))]">
                            {option.label}
                          </span>
                          {option.sublabel ? (
                            <span className="mt-0.5 block truncate text-xs text-[hsl(var(--muted-foreground))]">
                              {option.sublabel}
                            </span>
                          ) : null}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

export function AssignRolesPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedRole, setSelectedRole] = useState<AssignOption | null>(null);
  const [selectedUser, setSelectedUser] = useState<AssignOption | null>(null);

  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [itemsPerPage, setItemsPerPage] = useState(
    Math.min(parseInt(searchParams.get('limit') || '10', 10) || 10, 200)
  );
  const [roleFilter, setRoleFilter] = useState<string>(searchParams.get('role') || "all");
  const {
    inputValue: searchQuery,
    submittedValue: submittedSearch,
    setInputValue: setSearchQuery,
    clear: clearSearch,
    onKeyDown: onSearchKeyDown,
  } = useDebouncedSearch(searchParams.get('search') || "", {
    onCommit: () => setCurrentPage(1),
  });

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);

  // 1️⃣ FETCH ADMIN ROLES
  const rolesQuery = useQuery({
    queryKey: ["admin-roles"],
    queryFn: async () => {
      const res = await authFetch(
        `${API_URL}/assign-role/roles?portalType=${FIXED_PORTAL}`
      );
      const json = await res.json();
      return json.data || [];
    },
  });

  // 2️⃣ USER ASSIGNMENT STATS (counts only)
  const userStatsQuery = useQuery({
    queryKey: ["admin-user-stats"],
    queryFn: async () => {
      const res = await authFetch(
        `${API_URL}/assign-role/users?portalType=${FIXED_PORTAL}&statsOnly=true`
      );
      const json = await res.json();
      return json.stats || { total: 0, assigned: 0, pending: 0 };
    },
  });

  const loadUserOptions = useCallback(async (inputValue: string) => {
    const params = new URLSearchParams({
      portalType: FIXED_PORTAL,
      page: "1",
      limit: "50",
    });
    if (inputValue.trim()) params.append("search", inputValue.trim());
    const res = await authFetch(`${API_URL}/assign-role/users?${params.toString()}`);
    const json = await res.json();
    return (json.data || []).map((u: any) => ({
      value: u._id,
      label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email,
      sublabel: u.email,
    }));
  }, []);

  // 3️⃣ FETCH PAGINATED USERS
  const usersQuery = useQuery({
    queryKey: ["admin-users", currentPage, itemsPerPage, submittedSearch, roleFilter],
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams({
        portalType: FIXED_PORTAL,
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
      });
      if (submittedSearch.trim()) params.append("search", submittedSearch);
      if (roleFilter !== "all") params.append("roleFilter", roleFilter);

      const res = await authFetch(
        `${API_URL}/assign-role/users?${params.toString()}`,
        { signal }
      );
      const json = await res.json();
      return json;
    },
  });

  const roles = rolesQuery.data || [];
  const users = usersQuery.data?.data || [];
  const pagination = usersQuery.data?.pagination || { page: 1, limit: 10, total: 0, totalPages: 0 };

  // ROLE OPTIONS
  const roleOptions: AssignOption[] = useMemo(() => roles.map((r: any) => ({
    label: r.name,
    value: r._id,
  })), [roles]);

  const stats = userStatsQuery.data || { total: 0, assigned: 0, pending: 0 };

  // 4️⃣ ASSIGN ROLE
  const assignRoleMutation = useMutation({
    mutationFn: async ({ userId, roleId }: any) => {
      const res = await authFetch(`${API_URL}/assign-role/assign/${userId}`, {
        method: "PUT",
        body: JSON.stringify({ roleId }),
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      queryClient.invalidateQueries({ queryKey: ["admin-user-stats"] });
      setSelectedRole(null);
      setSelectedUser(null);
      setEditModalOpen(false);
    },
  });

  // 5️⃣ REMOVE ROLE
  const removeRoleMutation = useMutation({
    mutationFn: async (userId: string) => {
      await authFetch(`${API_URL}/assign-role/assign/${userId}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      queryClient.invalidateQueries({ queryKey: ["admin-user-stats"] });
    },
  });

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (submittedSearch) params.set('search', submittedSearch);
    if (currentPage > 1) params.set('page', currentPage.toString());
    if (itemsPerPage !== 10) params.set('limit', itemsPerPage.toString());
    if (roleFilter !== 'all') params.set('role', roleFilter);
    setSearchParams(params, { replace: true });
  }, [submittedSearch, currentPage, itemsPerPage, roleFilter, setSearchParams]);

  useEffect(() => {
    setCurrentPage(1);
  }, [roleFilter]);

  return (
    <div className="p-8 space-y-8 bg-[hsl(var(--background))] min-h-screen">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-6">
        <div>
          <nav className="flex items-center gap-2 text-xs font-semibold text-[hsl(var(--muted-foreground))] mb-2 uppercase tracking-wider">
            <span>Enterprise</span>
            <span>/</span>
            <span>Acl Management</span>
          </nav>
          <h1 className="text-3xl font-bold text-[hsl(var(--foreground))] tracking-tight">
            Role Assignment
          </h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-2 max-w-2xl text-sm">
            Delegate responsibilities and manage access for your portal members.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-4 min-w-[360px]">
          <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#0088cc]/10 flex items-center justify-center text-[#0088cc]">
              <MdPeople className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Total Users</p>
              <p className="text-xl font-bold text-[hsl(var(--foreground))]">{stats.total}</p>
            </div>
          </div>
          <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
              <MdCheckCircle className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Assigned</p>
              <p className="text-xl font-bold text-[hsl(var(--foreground))]">{stats.assigned}</p>
            </div>
          </div>
          <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-600 dark:text-amber-400">
              <MdOutlinePending className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Pending</p>
              <p className="text-xl font-bold text-[hsl(var(--foreground))]">{stats.pending}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* ASSIGN ROLE CARD */}
        <div className="xl:col-span-1">
          <div className="sticky top-8 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-6 overflow-visible">
            <div className="relative">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[hsl(var(--border))]">
                <div className="w-10 h-10 rounded-lg bg-[#0088cc] flex items-center justify-center">
                  <MdPersonAdd className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-[hsl(var(--foreground))]">Fast Assign</h2>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">Link a member to a role</p>
                </div>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-semibold text-[hsl(var(--foreground))] mb-1.5">Select User</label>
                  <AssignDropdown
                    searchable
                    loadOptions={loadUserOptions}
                    placeholder="Search members..."
                    value={selectedUser}
                    onChange={setSelectedUser}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[hsl(var(--foreground))] mb-1.5">Assign Role</label>
                  <AssignDropdown
                    options={roleOptions}
                    placeholder="Choose role..."
                    value={selectedRole}
                    onChange={setSelectedRole}
                  />
                </div>
                <div className="pt-2">
                  <button
                    disabled={!selectedRole || !selectedUser || assignRoleMutation.isPending}
                    onClick={() =>
                      assignRoleMutation.mutate({
                        userId: selectedUser.value,
                        roleId: selectedRole.value,
                      })
                    }
                    className="w-full py-2.5 px-4 bg-[#0088cc] hover:bg-[#0077b3] text-white text-sm font-semibold rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {assignRoleMutation.isPending ? "Processing..." : "Confirm Assignment"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* USERS TABLE */}
        <div className="xl:col-span-2">
          <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl overflow-hidden flex flex-col h-full">
            <div className="p-4 border-b border-[hsl(var(--border))]">
              <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="relative w-full md:max-w-md">
                  <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] w-5 h-5" />
                  <input
                    type="text"
                    className="w-full pl-10 pr-4 py-2 text-sm rounded border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:border-[#0088cc] focus:ring-1 focus:ring-[#0088cc] outline-none transition-all"
                    placeholder="Search members..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={onSearchKeyDown}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-[hsl(var(--muted))] rounded border border-[hsl(var(--border))]">
                    <MdFilterList className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                    <select
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value)}
                      className="bg-transparent border-none text-xs font-semibold text-[hsl(var(--foreground))] outline-none cursor-pointer"
                    >
                      <option value="all">All Roles</option>
                      {roles.map((r: any) => (
                        <option key={r._id} value={r._id}>{r.name}</option>
                      ))}
                    </select>
                  </div>
                  <ClearFiltersButton
                    visible={!!searchQuery.trim() || roleFilter !== "all"}
                    onClick={() => {
                      clearSearch();
                      setRoleFilter("all");
                    }}
                    variant="toolbar"
                  />
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[hsl(var(--muted))] border-b border-[hsl(var(--border))]">
                    <th className="px-6 py-3 text-left text-xs font-bold text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Portal Member</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Permissions</th>
                    <th className="px-6 py-3 text-right text-xs font-bold text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[hsl(var(--border))]">
                  {usersQuery.isLoading ? (
                    <TableBodySkeleton rows={6} cols={4} />
                  ) : users.length === 0 ? (
                    <tr><td colSpan={4} className="px-6 py-12 text-center text-sm font-semibold text-[hsl(var(--muted-foreground))]">No members found</td></tr>
                  ) : (
                    users.map((u: any) => (
                      <tr key={u._id} className="hover:bg-[hsl(var(--muted))]/50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#0088cc]/10 flex items-center justify-center">
                              <span className="text-base font-bold text-[#0088cc] uppercase">{u.firstName?.[0] || "U"}</span>
                            </div>
                            <div className="text-sm font-semibold text-[hsl(var(--foreground))]">{u.firstName} {u.lastName}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-[hsl(var(--muted-foreground))]">{u.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {u.roleName ? (
                            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0088cc]/10 border border-[#0088cc]/20 w-fit">
                              <MdShield className="w-3.5 h-3.5 text-[#0088cc]" />
                              <span className="text-xs font-bold text-[#0088cc] uppercase">{u.roleName}</span>
                            </div>
                          ) : (
                            <span className="text-xs font-semibold text-[hsl(var(--muted-foreground))] italic">No Role</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => {
                                setEditingUser(u);
                                setEditingRoleId(u.roleId || null);
                                setEditModalOpen(true);
                              }}
                              title="Edit Role"
                              className="p-2 rounded hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:text-[#0088cc] transition-colors"
                            ><MdEdit className="w-4 h-4" /></button>
                            <button
                              onClick={() => {
                                if (confirm(`Revoke access from ${u.firstName}?`)) removeRoleMutation.mutate(u._id);
                              }}
                              title="Remove Role"
                              className="p-2 rounded hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:text-red-600 transition-colors"
                            ><MdDelete className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="border-t border-[hsl(var(--border))] p-4">
              {pagination.total > 0 && (
                <Pagination
                  currentPage={pagination.page}
                  totalPages={pagination.totalPages}
                  onPageChange={setCurrentPage}
                  itemsPerPage={pagination.limit}
                  totalItems={pagination.total}
                  onItemsPerPageChange={(newItemsPerPage) => {
                    setItemsPerPage(newItemsPerPage);
                    setCurrentPage(1);
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* EDIT MODAL */}
      {editModalOpen && editingUser && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl w-full max-w-lg relative overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-[hsl(var(--border))] relative">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#0088cc]/10 flex items-center justify-center text-[#0088cc] font-bold text-xl">
                  {editingUser.firstName?.[0] || 'U'}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-[hsl(var(--foreground))]">Adjust Access</h2>
                  <p className="text-sm font-semibold text-[hsl(var(--muted-foreground))]">Updating {editingUser.firstName}</p>
                </div>
              </div>
              <button onClick={() => setEditModalOpen(false)} className="p-2 rounded hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] transition-colors"><MdClose className="w-6 h-6" /></button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-[hsl(var(--foreground))]">Member</label>
                  <div className="px-3 py-2 rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))] text-sm text-[hsl(var(--foreground))] font-medium">{editingUser.firstName} {editingUser.lastName}</div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-[hsl(var(--foreground))]">Email</label>
                  <div className="px-3 py-2 rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))] text-sm text-[hsl(var(--foreground))] font-medium truncate">{editingUser.email}</div>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-[hsl(var(--foreground))]">Authorize New Role</label>
                <AssignDropdown
                  options={roleOptions}
                  placeholder="Choose role..."
                  value={roleOptions.find((r) => r.value === editingRoleId) || null}
                  onChange={(opt) => setEditingRoleId(opt?.value || null)}
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button onClick={() => setEditModalOpen(false)} className="flex-1 py-2 rounded border border-[hsl(var(--border))] font-semibold text-sm text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] transition-colors">Discard</button>
                <button
                  onClick={() => editingRoleId && assignRoleMutation.mutate({ userId: editingUser._id, roleId: editingRoleId })}
                  disabled={!editingRoleId || assignRoleMutation.isPending}
                  className="flex-1 py-2 rounded bg-[#0088cc] text-white font-semibold text-sm hover:bg-[#0077b3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {assignRoleMutation.isPending ? "Updating..." : "Apply Authority"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
